import { action, makeObservable, observable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { HasParent } from "./Parent";
import { ChatMessageGroup } from "./ChatMessageGroup";

export type ChatMessageRenderMode = "htmlDocument";

export interface IChatMessage extends HasParent<ChatMessageGroup> {
    readonly id: string;
    content: string;
    dynamicContent?: () => string;
    renderMode?: ChatMessageRenderMode;

    persist(
        additionalInformation?: Record<string, unknown>,
    ): Promise<IChatMessage>;

    toString(): string;
}

export class ChatMessage implements IChatMessage {
    readonly id: string;

    public readonly parent: ChatMessageGroup;

    @observable content: string;
    @observable cancelled: boolean;
    readonly renderMode?: ChatMessageRenderMode;

    constructor(content: string);
    constructor(
        messageGroup: ChatMessageGroup,
        content: string,
        renderMode?: ChatMessageRenderMode,
    );
    constructor(
        contentOrMessageGroup: string | ChatMessageGroup,
        content?: string,
        renderMode?: ChatMessageRenderMode,
    ) {
        makeObservable(this);

        this.id = uuidv4();
        if (
            content === undefined &&
            typeof contentOrMessageGroup === "string"
        ) {
            this.content = contentOrMessageGroup;
        } else if (
            typeof content === "string" &&
            contentOrMessageGroup instanceof ChatMessageGroup
        ) {
            this.parent = contentOrMessageGroup;
            this.content = content;
            this.renderMode = renderMode;
        } else {
            throw new Error(
                "Invalid ChatMessage constructor usage. See available overloads.",
            );
        }
        this.cancelled = false;
    }

    @action public setContent(content: string) {
        this.content = content;
    }

    @action public appendContent(content: string) {
        this.content += content;
    }

    @action public setCancelled(cancelled: boolean) {
        this.cancelled = cancelled;
    }

    public async persist(
        additionalInformation?: Record<string, unknown>,
    ): Promise<ChatMessage> {
        if (!this.parent) {
            throw new Error(
                "Cannot persist standalone message. Message must be part of a message group in a chat.",
            );
        }

        const messageGroup = this.parent;
        const chat = messageGroup.parent;

        await chat.service.persistMessage(chat.id, {
            author: messageGroup.author,
            content: this.content,
            additionalInformation,
        });

        return this;
    }

    public toString() {
        return this.content;
    }
}

export class TemplatedChatMessage implements IChatMessage {
    readonly id: string;

    public readonly parent: ChatMessageGroup;

    @observable arg: unknown;
    @observable content: string;
    @observable buildContent: (arg: unknown) => string;
    @observable dynamicContent = () => this.buildContent(this.arg);

    constructor(
        parent: ChatMessageGroup,
        arg: unknown,
        buildContent: (arg: unknown) => string,
    ) {
        makeObservable(this);
        this.parent = parent;
        this.id = uuidv4();
        this.arg = arg;
        this.buildContent = buildContent;
    }

    public async persist(
        additionalInformation?: Record<string, unknown>,
    ): Promise<TemplatedChatMessage> {
        const messageGroup = this.parent;
        const chat = messageGroup.parent;

        await chat.service.persistMessage(chat.id, {
            author: messageGroup.author,
            content: this.dynamicContent(),
            additionalInformation,
        });

        return this;
    }

    public toString(): string {
        return this.dynamicContent();
    }
}
